import { useCallback, useEffect, useRef, useState } from "react";
import { restGet } from "../../lib/restClient.js";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialCopy } from "../../lib/denialReason.js";
import { getPerplexityAuditIntegration } from "../../lib/perplexityAuditClient.js";
import { listLangSmithIntegrations } from "../../lib/langsmithClient.js";
import { listCopilotIntegrations } from "../../lib/copilotClient.js";
import { listOpenAiIntegrations } from "../../lib/openaiClient.js";
import { listDirectoryIntegrations } from "../../lib/identityClient.js";
import { listVertexIntegrations } from "../../lib/vertexClient.js";
import {
    copilotIntegration,
    keyIntegrationSummary,
    langsmithIntegration,
    openaiIntegration,
    perplexitySummary,
    vertexIntegration,
} from "./model.js";

const LOADERS = {
    anthropic: async () => keyIntegrationSummary(await restGet("/v1/compliance-integration")),
    openai: async () =>
        ((await listOpenAiIntegrations())?.integrations ?? []).map(openaiIntegration),
    perplexity: async () => perplexitySummary(await getPerplexityAuditIntegration()),
    langsmith: async () =>
        ((await listLangSmithIntegrations())?.integrations ?? []).map(langsmithIntegration),
    copilot: async () =>
        ((await listCopilotIntegrations())?.integrations ?? []).map(copilotIntegration),
    vertex: async () =>
        ((await listVertexIntegrations())?.integrations ?? []).map(vertexIntegration),
};

// Loads every visible source once and keeps the results while the admin moves
// between the health view and a source's page. A reload replaces one source's
// data only when the fetch succeeds, so the previous status stays on screen
// while it is in flight.
export function useDataSources(keys) {
    const caps = useMyPermissions();
    // The permission posture is re-published periodically; reading it through
    // a ref keeps that revalidation from refetching every source.
    const capsRef = useRef(caps);
    capsRef.current = caps;
    const [entries, setEntries] = useState(() =>
        Object.fromEntries(keys.map((key) => [key, { data: null, error: null, loading: true, loaded: false }])),
    );
    const generation = useRef({});
    const [directory, setDirectory] = useState({ data: null, error: null, loading: true, loaded: false });

    useEffect(() => {
        let cancelled = false;
        listDirectoryIntegrations()
            .then((data) => {
                if (!cancelled) setDirectory({ data, error: null, loading: false, loaded: true });
            })
            .catch((err) => {
                if (!cancelled) {
                    setDirectory({
                        data: null,
                        error: denialCopy(err, capsRef.current).message,
                        loading: false,
                        loaded: true,
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const reload = useCallback(
        async (key) => {
            const loader = LOADERS[key];
            if (!loader) return;
            const token = (generation.current[key] ?? 0) + 1;
            generation.current[key] = token;
            setEntries((current) => ({
                ...current,
                [key]: { ...(current[key] ?? {}), loading: true, error: null },
            }));
            try {
                const data = await loader();
                if (generation.current[key] !== token) return;
                setEntries((current) => ({
                    ...current,
                    [key]: { data, error: null, loading: false, loaded: true },
                }));
            } catch (err) {
                if (generation.current[key] !== token) return;
                setEntries((current) => ({
                    ...current,
                    [key]: {
                        ...(current[key] ?? {}),
                        error: denialCopy(err, capsRef.current).message,
                        loading: false,
                        loaded: true,
                    },
                }));
            }
        },
        [],
    );

    const keysSignature = keys.join(",");
    useEffect(() => {
        for (const key of keysSignature.split(",")) reload(key);
    }, [keysSignature, reload]);

    const replaceData = useCallback((key, data) => {
        setEntries((current) => ({
            ...current,
            [key]: { data, error: null, loading: false, loaded: true },
        }));
    }, []);

    return { entries, directory, reload, replaceData };
}
